import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import Loading from '@components-clean/core/loading/loading';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { TextBoxController } from '@components-clean/core/text-box';
import { sortDatasetByDatesDesc } from '@components-clean/navbar/dataset-selector.utils';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { MultiSelectKeyValue, MultiSelectOption } from '@interfaces-clean/dropdowns';
import { ExistingReview, SubjectMonitoringReviewPostDto } from '@interfaces-clean/reviews.interface';
import { isValidReviewTitle } from '@utils-clean/validation';
import { getSessionStorageDatasetId } from '@contexts/local-state-context';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import { ReviewPlanAPI } from '@apis/bff-api/study/review-plans';
import { SubjectMonitoringStudyMemberApi } from '@apis/bff-api/subject-monitoring/subject-monitoring-study-member-api';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type AddReviewDialogProps = {
    studyRef: string;
    onClose: () => void;
    onSubmit: (addedId?: number, addedRecordId?: number, datasetId?: number, type?: string) => void;
    reviews: ExistingReview[];
    preselectedDatasetId?: number;
    preselectedReviewTypeId?: number;
};

interface IDatasetDropdownItem {
    id: number;
    label: string;
}
interface INewReviewForm {
    dataset?: IDatasetDropdownItem;
    reviewType: MultiSelectOption;
    reviewTitle: string;
    reviewers?: MultiSelectKeyValue[];
}

const COMPONENT_NAME = 'add-subject-monitoring-review-dialog';

const AddSubjectMonitoringReviewDialog = ({
    reviews,
    studyRef,
    onClose,
    onSubmit,
    preselectedReviewTypeId,
    preselectedDatasetId,
}: AddReviewDialogProps) => {
    const datasetId = preselectedDatasetId ?? parseInt(getSessionStorageDatasetId() ?? '0', 10);
    const { t } = useTranslation();
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });
    const { trigger: loadStudyMembers, data: studyMembersData } = SubjectMonitoringStudyMemberApi.useLazyGet(studyRef);
    const { trigger: postReview, isMutating: isSavingReview } = SubjectMonitoringReviewAPI.usePostReview(studyRef);

    const [dropdownReviewTypes, setDropdownReviewTypes] = useState<MultiSelectOption[]>([]);
    const [dropdownStudyMembers, setDropdownStudyMembers] = useState<MultiSelectKeyValue[]>([]);

    const defaultValues = {
        dataset: undefined,
        reviewers: [],
        reviewTitle: '',
        reviewType: undefined,
    };

    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid },
        getValues,
        trigger: revalidate,
        resetField,
        reset,
        setValue,
    } = useForm<INewReviewForm>({
        mode: 'all',
        defaultValues,
    });

    const {
        data: cm2DataSetsData,
        isValidating: validatingCM2Data,
        isLoading: loadingCM2Data,
    } = DatasetApi.useGetAll(studyRef);

    const { data: reviewPlans, isLoading: loadingReviewPlans } = ReviewPlanAPI.useGetAll(studyRef);

    const isLoading = loadingCM2Data || validatingCM2Data || loadingReviewPlans;

    const dropdownDatasets: IDatasetDropdownItem[] = useMemo(() => {
        if (!cm2DataSetsData) {
            return [];
        }

        const datasetItems = (cm2DataSetsData ?? [])
            .filter((dataset) => dataset.active)
            .sort(sortDatasetByDatesDesc)
            .map((d) => ({
                id: d.id!,
                label: stringDateWithoutTimeFromUtc(d?.dataCutDate ?? null, 'DD-MMM-YYYY') as string,
            }));

        setValue(
            'dataset',
            datasetItems.find((item) => item.id === datasetId),
        );

        return datasetItems;
    }, [cm2DataSetsData]);

    useEffect(() => {
        if (!reviewPlans) {
            return;
        }

        // A review type is selectable when both its study Review Plan and the org-level
        // Review Type are active. The GetAll endpoint already excludes plans where both
        // are inactive.
        const reviewTypes: MultiSelectOption[] = reviewPlans
            .map((plan) => {
                const selectable = (plan.active ?? false) && (plan.reviewTypeActive ?? false);
                return {
                    id: plan.reviewTypeId!,
                    value: plan.reviewTypeName!,
                    active: selectable,
                    groupedBy: selectable ? 'Active' : 'Inactive',
                    disabled: !selectable,
                };
            })
            .sort((a, b) => {
                // Sort by active first, disabled after
                if (a.disabled !== b.disabled) {
                    return a.disabled ? 1 : -1;
                }
                return a.value.localeCompare(b.value);
            });
        setDropdownReviewTypes(reviewTypes);

        if (preselectedReviewTypeId !== undefined) {
            const preselected = reviewTypes.find((x) => x.id === preselectedReviewTypeId);
            if (preselected && !preselected.disabled) {
                setValue('reviewType', preselected);
            }
        }
    }, [reviewPlans]);

    useEffect(() => {
        if (studyMembersData) {
            const multiSelectData: MultiSelectKeyValue[] = studyMembersData.map((sm) => ({
                id: sm.ref!,
                value: sm.name!,
            }));
            setDropdownStudyMembers(multiSelectData);
        }
    }, [studyMembersData]);

    useEffect(() => {
        loadStudyMembers().catch((err) => window.console.log(err));
    }, [loadStudyMembers]);

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        reset(defaultValues);
        onClose();
    };

    const onSubmitHandler = async () =>
        handleSubmit(async (values: INewReviewForm) => {
            if (!values.reviewTitle || !values.reviewType) {
                return;
            }

            const selectedDatasetId = values.dataset?.id;

            if (!selectedDatasetId) {
                return;
            }

            let studyMemberRefs: string[] = [];
            if (values.reviewers?.length && values.reviewers.length > 0) {
                studyMemberRefs = values.reviewers.map((item) => item.id);
            }

            const review: SubjectMonitoringReviewPostDto = {
                type: values.reviewType.value,
                title: values.reviewTitle,
                datasetId: selectedDatasetId,
                studyMemberRefs,
            };

            try {
                const result = await postReview(review);
                ShowSuccess(t('actionsSuccessfulToast'));
                onSubmit(result.reviewId, result.recordId, selectedDatasetId, review.type);
            } catch {
                ShowError(t('errorMessage'));
                onClose();
            }
        })();

    return (
        <DraggableDialog
            dataTestId="create-review-dialog"
            onClose={onCloseHandler}
            onSubmit={onSubmitHandler}
            title={mtkTranslation('addReviewsModal.title')}
            primaryBtnDisabled={!isDirty || !isValid || isSavingReview || isLoading}
            primaryBtnLabel={t('common:save')}
            secondaryBtnDisabled={isSavingReview}
            height={350}
        >
            <div className={cn('k-w-full', 'k-h-full', 'k-d-flex-col')}>
                {isLoading ? (
                    <Loading />
                ) : (
                    <>
                        <BasicDropDownController
                            activeField="active"
                            control={control}
                            name="dataset"
                            data={dropdownDatasets}
                            data-testid="dataset-dropdown"
                            label={mtkTranslation('addReviewsModal.datasetLabel')}
                            textField="label"
                            dataItemKey="id"
                            filterable
                            allowClear
                            readOnly={isSavingReview}
                            required
                        />
                        <BasicDropDownController
                            activeField="active"
                            control={control}
                            name="reviewType"
                            data={dropdownReviewTypes}
                            data-testid="new-review-type"
                            label={mtkTranslation('addReviewsModal.typeLabel')}
                            textField="value"
                            dataItemKey="id"
                            filterable
                            allowClear
                            readOnly={isSavingReview}
                            required
                            onChange={() => {
                                resetField('reviewTitle');
                            }}
                        />
                        <TextBoxController
                            control={control}
                            name="reviewTitle"
                            data-testid="new-review-title"
                            label={mtkTranslation('addReviewsModal.titleLabel')}
                            disabled={isSavingReview || typeof getValues()?.reviewType?.value === 'undefined'}
                            maxLength={30}
                            required
                            onChange={async () => {
                                await revalidate();
                            }}
                            validateRules={{
                                noInvalidCharacters: (value) => {
                                    if (value && !isValidReviewTitle(value as string)) {
                                        return mtkTranslation('addReviewsModal.invalidChars');
                                    }
                                    return true;
                                },
                                checkExistingReviews: (value) => {
                                    if (value) {
                                        const trimmed = (value as string).trim().toLowerCase();
                                        if (
                                            reviews.some(
                                                (review) =>
                                                    review.title.toLowerCase() === trimmed &&
                                                    review.type === getValues()?.reviewType?.value,
                                            )
                                        ) {
                                            return mtkTranslation('addReviewsModal.reviewAlreadyExists');
                                        }
                                    }
                                    return true;
                                },
                            }}
                        />
                        <MultiSelectController
                            isEditing
                            control={control}
                            name="reviewers"
                            data-testid="new-review-lovs"
                            data={dropdownStudyMembers}
                            textField="value"
                            dataItemKey="id"
                            label={mtkTranslation('addReviewsModal.reviewersLabel')}
                            disabled={isSavingReview}
                            allowCustom={false}
                        />
                    </>
                )}
            </div>
        </DraggableDialog>
    );
};

export default AddSubjectMonitoringReviewDialog;
